// partners/adminView/pages/A3_AdminApplicationDetail.jsx
// Full application detail + approve / reject / waitlist form.

import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  useGetAdminApplicationDetailQuery,
  useReviewApplicationMutation,
} from "../../../apis/adminApiSlice";
import AdminLayout from "./A0_AdminLayout";
import DeleteApplicationModal from "../DeleteApplicationModal";
import { capturePartnerEvent } from "../../../utils/analytics";

export default function AdminApplicationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: app, isLoading } = useGetAdminApplicationDetailQuery(id);
  const [reviewApplication, { isLoading: submitting }] =
    useReviewApplicationMutation();

  const [action, setAction] = useState("approve");
  const [notes, setNotes] = useState("");
  const [score, setScore] = useState("");
  const [reason, setReason] = useState("");
  const [feedback, setFeedback] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const viewedRef = useRef(false);

  useEffect(() => {
    if (app && !viewedRef.current) {
      viewedRef.current = true;
      capturePartnerEvent("partner_admin_application_viewed", {
        application_id: id,
        status: app.status,
        partner_type: app.partner_type,
      });
    }
  }, [app, id]);

  if (isLoading)
    return (
      <AdminLayout>
        <div className="text-mnf-text-muted text-sm">Loading...</div>
      </AdminLayout>
    );
  if (!app)
    return (
      <AdminLayout>
        <div className="text-mnf-error text-sm">Application not found.</div>
      </AdminLayout>
    );

  const handleSubmit = async () => {
    try {
      const result = await reviewApplication({
        id,
        action,
        notes,
        score: score ? parseInt(score, 10) : undefined,
        rejection_reason: reason,
      }).unwrap();
      if (action === "approve" && result.email_sent === false) {
        capturePartnerEvent("partner_admin_application_reviewed", {
          application_id: id,
          action,
          email_sent: false,
        });
        setFeedback(
          `Approved, but email was not sent${result.email_error ? `: ${result.email_error}` : "."}`
        );
        return;
      }
      capturePartnerEvent("partner_admin_application_reviewed", {
        application_id: id,
        action,
        email_sent:
          action === "approve" ? Boolean(result.email_sent) : undefined,
      });
      if (action === "approve" && result.email_sent) {
        setFeedback(`Application approved. Email sent to ${result.user_email}.`);
      } else {
        setFeedback(`Application ${action}d successfully.`);
      }
      setTimeout(() => navigate("/partners/admin/applications"), 1500);
    } catch (e) {
      capturePartnerEvent("partner_admin_application_review_failed", {
        application_id: id,
        action,
      });
      setFeedback(`Error: ${e?.data?.detail || "Action failed."}`);
    }
  };

  const field = (label, value) =>
    value ? (
      <div className="mb-3">
        <div className="text-xs text-mnf-text-muted uppercase tracking-wider font-poppins mb-0.5">
          {label}
        </div>
        <div className="text-sm text-mnf-text-primary">{value}</div>
      </div>
    ) : null;

  return (
    <AdminLayout>
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate(-1)}
          className="text-mnf-text-muted hover:text-mnf-cyan text-sm transition-colors"
        >
          ← Back
        </button>
        <h1 className="font-poppins text-2xl font-bold">{app.full_name}</h1>
        <span className="text-xs font-poppins font-semibold px-2.5 py-1 rounded-full bg-mnf-dark-3 text-mnf-text-secondary capitalize">
          {app.status?.replace("_", " ")}
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Details */}
        <div className="bg-mnf-dark-2 border border-white/[0.08] rounded-mnf-lg p-6">
          <h2 className="font-poppins text-sm font-semibold text-mnf-text-muted uppercase tracking-widest mb-5">
            Application Details
          </h2>
          {field(
            "Track",
            app.partner_type === "creative_ambassador"
              ? "Creative Ambassador"
              : "Film Ecosystem Partner"
          )}
          {field("Email", app.email)}
          {field("Phone", app.phone)}
          {field("City", app.city)}
          {field("PAN", app.pan_number ? "Provided" : "Not provided")}
          {field("Payment method", app.payment_method)}

          {app.partner_type === "ecosystem" && (
            <>
              {field("Business name", app.business_name)}
              {field("Business category", app.business_category)}
              {field("Website", app.website)}
              {field("Years operating", app.years_operating)}
              {field("Community size", app.community_size)}
              {field("Interaction method", app.interaction_method)}
            </>
          )}

          {app.partner_type === "creative_ambassador" && (
            <>
              {field("Channel name", app.channel_name)}
              {field("Platform", app.platform)}
              {field("Channel URL", app.channel_url)}
              {field("Followers", app.follower_count)}
              {field("Niche", app.content_niche)}
              {field("Avg engagement", app.avg_engagement)}
            </>
          )}

          {field("Motivation", app.motivation)}
          {field("Expected referrals", app.expected_referrals)}
          {field("Used MNF before?", app.used_mnf)}
          {field("Additional notes", app.additional_notes)}
        </div>

        {/* Review */}
        <div className="bg-mnf-dark-2 border border-white/[0.08] rounded-mnf-lg p-6">
          <h2 className="font-poppins text-sm font-semibold text-mnf-text-muted uppercase tracking-widest mb-5">
            Review Decision
          </h2>

          {app.status !== "pending" && app.status !== "under_review" && (
            <div className="mb-4 p-3 bg-mnf-dark-3 rounded-mnf text-xs text-mnf-text-muted">
              Already reviewed — notes: {app.review_notes || "None"} | Score:{" "}
              {app.review_score ?? "None"}
            </div>
          )}

          <div className="mb-4">
            <label className="block font-poppins text-xs font-semibold text-mnf-text-secondary mb-2">
              Action
            </label>
            <div className="flex gap-2">
              {["approve", "reject", "waitlist"].map((a) => (
                <button
                  key={a}
                  onClick={() => setAction(a)}
                  className={`px-4 py-2 rounded-mnf text-xs font-poppins font-semibold capitalize transition-all ${
                    action === a
                      ? "bg-mnf-gradient text-mnf-btn-text"
                      : "bg-mnf-dark-3 text-mnf-text-secondary hover:bg-mnf-dark-4"
                  }`}
                >
                  {a}
                </button>
              ))}
            </div>
          </div>

          <div className="mb-4">
            <label className="block font-poppins text-xs font-semibold text-mnf-text-secondary mb-1.5">
              Score (1–10)
            </label>
            <input
              type="number"
              min="1"
              max="10"
              value={score}
              onChange={(e) => setScore(e.target.value)}
              className="w-full px-3 py-2 bg-mnf-dark-3 border border-white/[0.08] rounded-mnf text-sm text-mnf-text-primary focus:outline-none focus:border-mnf-cyan"
            />
          </div>

          <div className="mb-4">
            <label className="block font-poppins text-xs font-semibold text-mnf-text-secondary mb-1.5">
              Internal notes
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 bg-mnf-dark-3 border border-white/[0.08] rounded-mnf text-sm text-mnf-text-primary resize-none focus:outline-none focus:border-mnf-cyan"
            />
          </div>

          {action === "reject" && (
            <div className="mb-4">
              <label className="block font-poppins text-xs font-semibold text-mnf-text-secondary mb-1.5">
                Rejection reason (sent to applicant)
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 bg-mnf-dark-3 border border-white/[0.08] rounded-mnf text-sm text-mnf-text-primary resize-none focus:outline-none focus:border-mnf-cyan"
              />
            </div>
          )}

          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="w-full font-poppins font-semibold text-sm py-3 rounded-mnf-pill bg-mnf-gradient text-mnf-btn-text disabled:opacity-50 transition-opacity"
          >
            {submitting ? "Submitting..." : `Confirm ${action}`}
          </button>

          {feedback && (
            <div className="mt-3 text-xs text-center text-mnf-cyan">{feedback}</div>
          )}
        </div>

        {app.can_delete && (
          <div className="lg:col-span-2 bg-mnf-dark-2 border border-mnf-error/30 rounded-mnf-lg p-6">
            <h2 className="font-poppins text-sm font-semibold text-mnf-error uppercase tracking-widest mb-3">
              Delete application
            </h2>
            <p className="text-xs text-mnf-text-muted mb-4">
              Permanently removes this application so{" "}
              <span className="text-mnf-text-primary">{(app.email || "").trim()}</span>{" "}
              can submit again. Only available for pending, rejected, or waitlisted
              applications.
            </p>
            <button
              type="button"
              onClick={() => setShowDeleteModal(true)}
              className="text-xs font-poppins font-semibold px-4 py-2 rounded-mnf border border-mnf-error/50 text-mnf-error hover:bg-mnf-error/10 transition-colors"
            >
              Delete application
            </button>
          </div>
        )}
      </div>

      {showDeleteModal && (
        <DeleteApplicationModal
          application={app}
          onClose={() => setShowDeleteModal(false)}
          onDeleted={() => navigate("/partners/admin/applications")}
        />
      )}
    </AdminLayout>
  );
}
